import { FormikTouched, FormikValues, getIn, setIn } from "formik";
import isEqual from "lodash/isEqual";
import { BusinessFormValues } from "./types";

/**
 * Merges updates from the store (nextInitialValues) into the current form state (currentValues),
 * but only for fields that the user has not "touched" or manually changed (dirty).
 *
 * It treats arrays as atomic units — if any part of an array is touched or dirty,
 * the entire array is preserved as-is to avoid partial syncs which could lead to
 * inconsistent state (e.g. mismatched IDs).
 */
export function mergeUntouched(
  nextInitialValues: Partial<BusinessFormValues>,
  currentValues: FormikValues,
  touched: FormikTouched<BusinessFormValues>,
  previousInitialValues: Partial<BusinessFormValues>,
): FormikValues {
  let result = currentValues;

  function visit(path = "") {
    const nextVal = getIn(nextInitialValues, path);
    const prevVal = getIn(previousInitialValues, path);
    const currVal = getIn(currentValues, path);

    // Leaf node or whole object/array that is untouched and non-dirty
    const isTouched = getIn(touched, path);
    const isDirty = !isEqual(currVal, prevVal);

    if (!isTouched && !isDirty) {
      result = setIn(result, path, nextVal);
      return;
    }

    // If it's an object (and not array), recurse into its keys.
    // We check both nextVal and prevVal to ensure we catch keys that were added or removed.
    if (
      (nextVal && typeof nextVal === "object" && !Array.isArray(nextVal)) ||
      (prevVal && typeof prevVal === "object" && !Array.isArray(prevVal))
    ) {
      const keys = new Set([
        ...Object.keys(nextVal || {}),
        ...Object.keys(prevVal || {}),
      ]);
      keys.forEach(key => visit(path ? `${path}.${key}` : key));
      return;
    }
  }

  // Start visiting from the top-level keys
  const topLevelKeys = new Set([
    ...Object.keys(nextInitialValues),
    ...Object.keys(previousInitialValues),
  ]);
  topLevelKeys.forEach(key => visit(key));

  return result;
}
